import { useState } from "react";

import { useNow } from "src/hooks/useNow";
import { callNext, closeShift, completeEntry, openShift } from "src/api/client";
import type { QueueEntry, Shift } from "src/api/types";

interface Props {
  token: string;
  activeShift: Shift | null;
  activeShifts: Shift[];
  ownEmail: string;
  activeEntry: QueueEntry | null;
  hasWaitingPatients: boolean;
  onShiftChange: (shift: Shift | null) => void;
  onQueueRefetch: () => void;
  onPatientCalled: () => void;
  onError: (message: string) => void;
}

export function ShiftPanel({
  token,
  activeShift,
  activeShifts,
  ownEmail,
  activeEntry,
  hasWaitingPatients,
  onShiftChange,
  onQueueRefetch,
  onPatientCalled,
  onError,
}: Props) {
  const [busy, setBusy] = useState(false);
  const sessionTime = useShiftDuration(activeShift?.started_at);

  async function handleOpenShift() {
    setBusy(true);
    try {
      const shift = await openShift(token);
      onShiftChange(shift);
    } catch (err) {
      onError(errorMessage((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function handleCloseShift() {
    if (!activeShift) return;
    setBusy(true);
    try {
      const shift = await closeShift(token, activeShift.id);
      onShiftChange(shift);
      onQueueRefetch();
    } catch (err) {
      onError(errorMessage((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function handleCallNext() {
    setBusy(true);
    try {
      const entry = await callNext(token);
      if (entry) {
        onPatientCalled();
        onQueueRefetch();
      } else {
        onError("No patients waiting.");
      }
    } catch (err) {
      onError(errorMessage((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function handleCompleteActive() {
    if (!activeEntry) return;
    setBusy(true);
    try {
      await completeEntry(token, activeEntry.id);
      onQueueRefetch();
    } catch (err) {
      onError(errorMessage((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`provider-dashboard${activeShift ? "" : " idle"}`}>
      <div className="provider-dashboard-main">
        <div>
          <div className="provider-status-title">{queueStatus(activeShifts, ownEmail)}</div>
          <div className="provider-status-text">
            {activeShifts.length > 0 ? "Ready to call patients" : "Start a shift to open the queue"}
          </div>
        </div>
        <div className="provider-timer-wrap">
          <div className="provider-status-label">Session time</div>
          <div className="provider-timer">{sessionTime}</div>
        </div>
      </div>

      <div className="action-grid">
        {!activeShift && (
          <button
            className="btn btn-primary"
            onClick={() => void handleOpenShift()}
            disabled={busy}
          >
            Start my Shift
          </button>
        )}
        {hasWaitingPatients && (
          <button className="btn btn-primary" onClick={() => void handleCallNext()} disabled={busy}>
            Call Next Patient
          </button>
        )}
        {activeEntry && (
          <button
            className="btn btn-outline"
            onClick={() => void handleCompleteActive()}
            disabled={busy}
          >
            Complete Active
          </button>
        )}
        {activeShift && (
          <button
            className="btn btn-outline btn-danger"
            onClick={() => void handleCloseShift()}
            disabled={busy}
          >
            End Shift
          </button>
        )}
      </div>
    </div>
  );
}

function queueStatus(activeShifts: Shift[], ownEmail: string) {
  if (activeShifts.length === 0) {
    return "The queue is closed, no one has a shift";
  }

  if (activeShifts.length === 1) {
    const [shift] = activeShifts;
    return shift.email === ownEmail
      ? "The queue is open because your shift is active"
      : `The queue is open because ${shift.email} has an active shift`;
  }

  return (
    <>
      The queue is open because{" "}
      <span className="queue-status-hover" title={activeShifts.map((s) => s.email).join(", ")}>
        {activeShifts.length} providers
      </span>{" "}
      have active shifts
    </>
  );
}

function useShiftDuration(startedAt?: string): string {
  const now = useNow();

  if (!startedAt) return "--";
  // Clamp: client clock can trail the server's started_at.
  const mins = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 60_000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function errorMessage(code: string): string {
  switch (code) {
    case "not_a_taker":
      return "You don't have permission to perform that action.";
    case "shift_already_active":
      return "You already have an active shift.";
    case "no_active_shift":
      return "You must start a shift before calling a patient.";
    case "entry_in_progress":
      return "You already have a patient in progress. Complete it before calling next.";
    case "invalid_transition":
      return "That patient is no longer in progress.";
    default:
      return "Something went wrong. Please try again.";
  }
}
